import { Icon } from "@/components/ui";
import type { CSSProperties, ReactNode } from "react";

/**
 * Table - 고밀도 데이터 테이블 (VISUAL_DENSITY 8)
 *
 * 32행 x 12열을 한 화면에서 읽어야 한다. 그래서
 *  - 행 높이 36px, 셀 좌우 10px. 카드 박스 대신 1px 헤어라인.
 *  - 헤더는 sticky. 스크롤해도 열 이름이 남는다.
 *  - 헤더 밑은 1px divider 가 아니라 스크롤 엣지 그라디언트다
 *    (apple-design 12). 떠 있는 크롬이 콘텐츠를 실제로 덮는
 *    자리에만 경계가 생긴다.
 *  - 숫자 열은 data-num 으로 tabular-nums 를 강제한다.
 *
 * 지연 행 강조는 배경 틴트 + 좌측 3px 규칙선(inset box-shadow)
 * 이다. border 로 하면 셀 폭이 밀려서 열 정렬이 깨진다.
 */

export const TableScroll = ({
  children,
  bleed = 0,
  style,
  className,
}: {
  children: ReactNode;
  /** 카드 패딩을 상쇄해서 스크롤 영역을 화면 끝까지 밀어낸다 */
  bleed?: number;
  style?: CSSProperties;
  className?: string;
}) => (
  <div
    className={["relative overflow-x-auto", className ?? ""].join(" ")}
    style={{
      WebkitOverflowScrolling: "touch",
      margin: bleed ? `0 -${bleed}px` : undefined,
      padding: bleed ? `0 ${bleed}px` : undefined,
      ...style,
    }}
  >
    {children}
  </div>
);

export const Table = ({
  children,
  label,
  className,
}: {
  children: ReactNode;
  /** 스크린리더용 표 이름. 필수. */
  label: string;
  className?: string;
}) => (
  <table
    aria-label={label}
    className={["w-full border-collapse text-label", className ?? ""].join(" ")}
  >
    {children}
  </table>
);

/** sticky 헤더. 스크롤 엣지 효과는 jw-scroll-edge 가 만든다. */
export const THead = ({ children, sticky = true }: { children: ReactNode; sticky?: boolean }) => (
  <thead
    className={sticky ? "sticky top-0 z-sticky jw-scroll-edge" : undefined}
    style={{ background: "var(--surface-inset)" }}
  >
    {children}
  </thead>
);

export type SortDir = "asc" | "desc" | null;

export const Th = ({
  children,
  num = false,
  align,
  width,
  sort,
  onSort,
  scope = "col",
}: {
  children: ReactNode;
  num?: boolean;
  align?: "left" | "center" | "right";
  width?: number | string;
  /** null = 정렬 가능하지만 지금은 아님. undefined = 정렬 불가. */
  sort?: SortDir;
  onSort?: () => void;
  scope?: "col" | "row";
}) => {
  const a = align ?? (num ? "right" : "left");
  const sortable = onSort !== undefined;
  return (
    <th
      scope={scope}
      data-num={num ? "" : undefined}
      aria-sort={
        sortable
          ? sort === "asc"
            ? "ascending"
            : sort === "desc"
              ? "descending"
              : "none"
          : undefined
      }
      className="h-row-header whitespace-nowrap px-cell text-micro font-semibold uppercase"
      style={{
        textAlign: a,
        color: "var(--text-secondary)",
        width,
        borderBottom: "1px solid var(--border-subtle)",
      }}
    >
      {sortable ? (
        <button
          type="button"
          onClick={onSort}
          className="inline-flex items-center gap-s1 rounded-control uppercase hover:text-primary"
          style={{ color: sort ? "var(--accent)" : "inherit" }}
        >
          {children}
          <span aria-hidden="true" style={{ opacity: sort ? 1 : 0.35 }}>
            <Icon name={sort === "desc" ? "arrowDown" : "arrowUp"} size={12} stroke={2.2} />
          </span>
        </button>
      ) : (
        children
      )}
    </th>
  );
};

export const Tr = ({
  children,
  tone = "default",
  selected = false,
  onClick,
}: {
  children: ReactNode;
  /** "late" 는 지연 강조. 계획서 4/11 의 "지연 상단 표시"와 함께 쓴다. */
  tone?: "default" | "late";
  selected?: boolean;
  /**
   * 포인터 편의용일 뿐이다. 키보드 사용자를 위한 진짜 진입점은
   * 품명 셀 안의 링크/버튼이어야 한다. tr 을 포커스 가능하게
   * 만들면 32행 테이블에서 탭이 32번 걸린다.
   */
  onClick?: () => void;
}) => (
  <tr
    onClick={onClick}
    aria-selected={selected || undefined}
    data-tone={tone}
    className={[
      "group h-row transition-colors duration-fast ease-standard",
      onClick ? "cursor-pointer" : "",
      tone === "late" ? "bg-row-late hover:bg-row-late-hover" : "hover:bg-overlay-hover",
    ].join(" ")}
    style={{
      // 좌측 3px 규칙선 + 하단 헤어라인. border 대신 inset shadow 라
      // 셀 폭이 밀리지 않는다.
      boxShadow:
        tone === "late"
          ? "inset 3px 0 0 0 var(--state-late), inset 0 -1px 0 0 var(--border-subtle)"
          : "inset 0 -1px 0 0 var(--border-subtle)",
      background: selected ? "var(--accent-soft)" : undefined,
    }}
  >
    {children}
  </tr>
);

export const Td = ({
  children,
  num = false,
  align,
  strong = false,
  colSpan,
  className,
  style,
}: {
  children: ReactNode;
  num?: boolean;
  align?: "left" | "center" | "right";
  strong?: boolean;
  colSpan?: number;
  className?: string;
  style?: CSSProperties;
}) => {
  const a = align ?? (num ? "right" : "left");
  return (
    <td
      data-num={num ? "" : undefined}
      colSpan={colSpan}
      className={["whitespace-nowrap px-cell", className ?? ""].join(" ")}
      style={{
        textAlign: a,
        color: strong ? "var(--text-primary)" : "var(--text-secondary)",
        fontWeight: strong ? 600 : 400,
        ...style,
      }}
    >
      {children}
    </td>
  );
};

/**
 * 그룹 구분 행. 계획서 4/11 의 "지연 개발품 상단 자동 표시" 를
 * 이걸로 만든다. 지연이 0 건이면 화면 쪽에서 이 행을 아예 렌더하지
 * 않는다 (빈 그룹 헤더는 없다).
 */
export const TrGroup = ({
  label,
  count,
  colSpan,
  tone = "default",
}: {
  label: string;
  count?: number;
  colSpan: number;
  tone?: "default" | "late";
}) => (
  <tr>
    <th
      scope="colgroup"
      colSpan={colSpan}
      className="h-row-header px-cell text-left text-micro font-semibold uppercase"
      style={{
        background: tone === "late" ? "var(--state-late-soft)" : "var(--surface-inset)",
        color: tone === "late" ? "var(--state-late)" : "var(--text-secondary)",
        borderBottom: "1px solid var(--border-subtle)",
      }}
    >
      {label}
      {count !== undefined && <span className="tabular-nums"> {count}건</span>}
    </th>
  </tr>
);
